import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';

const AboutPage = () => {
  return (
    <div className="min-h-screen">
      <Header />
      <main>
        {/* Hero Section */}
        <section className="bg-gray-900 text-white py-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center">
              <h1 className="text-4xl md:text-5xl font-bold mb-6">
                About <span className="text-yellow-400">OhmEx Electric</span>
              </h1>
              <p className="text-xl text-gray-300 max-w-3xl mx-auto">
                Your trusted electrical contractor serving the Greater Toronto Area with professional, 
                reliable, and safe electrical services since our establishment.
              </p>
            </div>
          </div>
        </section>

        {/* Company Story */}
        <section className="py-16 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div>
                <h2 className="text-3xl font-bold text-gray-900 mb-6">Our Story</h2>
                <p className="text-lg text-gray-600 mb-6">
                  OhmEx Electric was founded with a simple mission: to provide exceptional electrical services 
                  to homeowners and businesses throughout the GTA. Based in Oshawa, Ontario, we have built 
                  our reputation on quality workmanship, reliability, and customer satisfaction.
                </p>
                <p className="text-lg text-gray-600 mb-6">
                  Our team of licensed and experienced electricians specializes in panel upgrades, EV charger 
                  installations, potlight installations, and comprehensive electrical solutions. We understand 
                  that electrical work requires precision, safety, and expertise – that's why we're committed 
                  to staying current with the latest electrical codes and technologies.
                </p>
                <div className="grid grid-cols-2 gap-6">
                  <div className="text-center">
                    <div className="text-3xl font-bold text-yellow-600">24/7</div>
                    <div className="text-gray-600">Emergency Service</div>
                  </div>
                  <div className="text-center">
                    <div className="text-3xl font-bold text-yellow-600">100%</div>
                    <div className="text-gray-600">Licensed & Insured</div>
                  </div>
                </div>
              </div>
              <div>
                <img
                  src="https://public.readdy.ai/ai/img_res/a1a7cd2ba6e10058f3b571a14694a4b2.jpg"
                  alt="OhmEx Electric Team"
                  className="w-full h-auto rounded-lg shadow-lg object-cover object-top"
                />
              </div>
            </div>
          </div>
        </section>

        {/* Values */}
        <section className="py-16 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">Our Values</h2>
              <p className="text-lg text-gray-600 max-w-2xl mx-auto">
                These core values guide everything we do at OhmEx Electric.
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <div className="text-center">
                <div className="w-16 h-16 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="ri-shield-check-fill text-yellow-600 text-2xl"></i>
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">Safety First</h3>
                <p className="text-gray-600">
                  Safety is our top priority. We follow all electrical codes and safety protocols 
                  to ensure every job is completed safely and correctly.
                </p>
              </div>
              <div className="text-center">
                <div className="w-16 h-16 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="ri-award-fill text-yellow-600 text-2xl"></i>
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">Quality Work</h3>
                <p className="text-gray-600">
                  We take pride in delivering high-quality electrical work that meets and exceeds 
                  industry standards and customer expectations.
                </p>
              </div>
              <div className="text-center">
                <div className="w-16 h-16 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="ri-customer-service-fill text-yellow-600 text-2xl"></i>
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">Customer Focus</h3>
                <p className="text-gray-600">
                  Our customers are at the heart of everything we do. We listen, communicate clearly, 
                  and ensure complete satisfaction with every project.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Team */}
        <section className="py-16 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">Meet Our Team</h2>
              <p className="text-lg text-gray-600 max-w-2xl mx-auto">
                Our experienced and licensed electricians are dedicated to providing exceptional service.
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <div className="text-center">
                <img
                  src="https://readdy.ai/api/search-image?query=Professional%20electrician%20portrait%2C%20experienced%20electrical%20contractor%2C%20safety%20helmet%20and%20tools%2C%20confident%20professional%20appearance%2C%20clean%20background%2C%20high%20quality%20photography&width=300&height=300&seq=team-1&orientation=squarish"
                  alt="Master Electrician"
                  className="w-32 h-32 rounded-full mx-auto mb-4 object-cover object-top"
                />
                <h3 className="text-xl font-semibold text-gray-900 mb-2">Master Electrician</h3>
                <p className="text-yellow-600 mb-3">Licensed Electrical Contractor</p>
                <p className="text-gray-600">
                  Over 15 years of experience in residential and commercial electrical work.
                </p>
              </div>
              <div className="text-center">
                <img
                  src="https://readdy.ai/api/search-image?query=Professional%20female%20electrician%20portrait%2C%20experienced%20electrical%20technician%2C%20safety%20gear%20and%20electrical%20tools%2C%20confident%20professional%20appearance%2C%20clean%20background&width=300&height=300&seq=team-2&orientation=squarish"
                  alt="Senior Electrician"
                  className="w-32 h-32 rounded-full mx-auto mb-4 object-cover object-top"
                />
                <h3 className="text-xl font-semibold text-gray-900 mb-2">Senior Electrician</h3>
                <p className="text-yellow-600 mb-3">Electrical Installation Specialist</p>
                <p className="text-gray-600">
                  Specializes in panel upgrades, EV charger installations, and modern electrical systems.
                </p>
              </div>
              <div className="text-center">
                <img
                  src="https://readdy.ai/api/search-image?query=Professional%20electrician%20apprentice%20portrait%2C%20young%20electrical%20technician%2C%20safety%20equipment%20and%20tools%2C%20enthusiastic%20professional%20appearance%2C%20clean%20background&width=300&height=300&seq=team-3&orientation=squarish"
                  alt="Electrical Technician"
                  className="w-32 h-32 rounded-full mx-auto mb-4 object-cover object-top"
                />
                <h3 className="text-xl font-semibold text-gray-900 mb-2">Electrical Technician</h3>
                <p className="text-yellow-600 mb-3">Lighting & Wiring Specialist</p>
                <p className="text-gray-600">
                  Expert in potlight installations, wiring services, and electrical troubleshooting.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-16 bg-yellow-400">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-3xl font-bold text-black mb-4">
              Ready to Work with OhmEx Electric?
            </h2>
            <p className="text-lg text-gray-800 mb-8 max-w-2xl mx-auto">
              Contact us today for a free estimate on your electrical project. We're here to help 
              with all your electrical needs in the GTA.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="https://wa.me/16472442797?text=Hi%20OhmEX%20Electric!%20I%20would%20like%20to%20get%20a%20free%20estimate%20for%20electrical%20services."
                className="bg-black text-white px-8 py-4 rounded-lg text-lg font-semibold hover:bg-gray-800 transition-colors cursor-pointer whitespace-nowrap inline-flex items-center justify-center space-x-2"
              >
                <i className="ri-whatsapp-fill"></i>
                <span>Get Free Estimate</span>
              </a>
              <a
                href="https://wa.me/16472442797?text=Hi%20OhmEX%20Electric!%20I%20would%20like%20to%20speak%20with%20you."
                className="bg-white text-black px-8 py-4 rounded-lg text-lg font-semibold hover:bg-gray-100 transition-colors cursor-pointer whitespace-nowrap inline-flex items-center justify-center space-x-2"
              >
                <i className="ri-phone-fill"></i>
                <span>Call Now</span>
              </a>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default AboutPage;
